import * as React from "react";
import {Creatable} from 'react-select';
import {Option, option} from "ts-option";
import {PlaceholderProps} from "../../common/props/PlaceholderProps";
import {LoadableProps} from "../../common/props/LoadableProps";
import {ClearableProps} from "../../common/props/ClearableProps";
import {InputActionMeta} from "react-select/lib/types";
import MultiValue, {MultiValueRemove} from "react-select/lib/components/MultiValue";
import {SortableContainer, SortableElement, SortEnd} from 'react-sortable-hoc';
import {arrayMove} from "../../../utils/ArrayUtils";

const SortableMultiValue = SortableElement(MultiValue);
const SortableCreatable = SortableContainer(Creatable);

export interface SelectComponentComponentProps<T> {
	/**
	 * All items.
	 */
	items: Option<T[]>;
	/**
	 * Selected
	 */
	value: Option<T[]>;
	/**
	 * The value of the search input.
	 */
	inputValue?: string;
	/**
	 * Callback on changed selection.
	 * @param selected element.
	 */
	onChanged: (selected: Option<T[]>) => void;
	/**
	 * Handle change events on the input.
	 * @param {string} value
	 * @param {InputActionMeta} action
	 */
	onInputChange?: (value: string, action: InputActionMeta) => void;
	/**
	 * Add new option callback
	 */
	onAddOption: (newOption: string) => void
	/**
	 * Clearable flag.
	 */
	clearable?: boolean
	/**
	 * No results default text.
	 */
	noResultsText?: string
	/**
	 * Prompt text creator
	 * @param {string} filterText
	 * @returns {string}
	 */
	promptTextCreator?: (filterText: string) => React.ReactNode;
	/**
	 * On input key down callback
	 */
	onInputKeyDown?: () => void
	/**
	 * Returns the data for the new option when it is created.
	 * @param {string} value
	 * @param {string} label
	 * @returns {T}
	 */
	getNewOptionData?: (value: string, label: string) => T;
	/**
	 * Determines whether the "create new ..." option should be displayed based on the current input value, select value and options array.
	 * @param {string} value
	 * @returns {boolean}
	 */
	isValidNewOption?: (value: string) => boolean;
	/**
	 * Backspace removes value
	 */
	backspaceRemovesValue?: boolean
}

type Props<T> = SelectComponentComponentProps<T> & PlaceholderProps & LoadableProps & ClearableProps

/**
 * Component for creation.
 */
export default abstract class CreatableSortableComponent<T> extends React.PureComponent<Props<T>, {}> {
	public static defaultProps: Partial<Props<any>> = {
		isValidNewOption: () => true
	};

	render() {
		return (
			<SortableCreatable
				value={this.props.value.orNull}
				classNamePrefix={"c-select-component"}
				inputValue={this.props.inputValue}
				options={this.props.items.getOrElse([])}
				styles={{
					indicatorsContainer: (base) => ({...base, display: 'none'}),
					multiValue: (base => ({...base, zIndex: 10000, cursor: "move"}))
				}}
				onChange={(val?: T[]) => this.props.onChanged(option(val))}
				isMulti={true}
				onCreateOption={this.props.onAddOption}
				placeholder={this.props.placeholder}
				isClearable={this.props.clearable}
				noOptionsMessage={() => this.props.noResultsText}
				onInputChange={this.props.onInputChange}
				getOptionValue={this.elementToText}
				getOptionLabel={this.elementToText}
				onKeyDown={this.props.onInputKeyDown}
				isLoading={this.props.loading}
				tabSelectsValue={false}
				isValidNewOption={this.props.isValidNewOption}
				formatCreateLabel={(filterText: string) => this.props.promptTextCreator(filterText)}
				createOptionPosition={"last"}
				closeMenuOnSelect={false}
				openMenuOnFocus={false}
				getNewOptionData={this.props.getNewOptionData}
				components={{
					MultiValue: (props) => <SortableMultiValue {...props} index={props.data.index}/>,
					MultiValueRemove: this.renderMultiValueRemove
				}}
				backspaceRemovesValue={this.props.backspaceRemovesValue}
				axis="xy"
				onSortEnd={this.onSortEnd}
				distance={5}
				lockToContainerEdges={true}
				getHelperDimensions={({node}) => node.getBoundingClientRect()}
			/>
		)
	}

	renderMultiValueRemove(props: any) {
		return <MultiValueRemove {...props}/>
	}

	onSortEnd = (sort: SortEnd) => {
		this.props.onChanged(option(arrayMove(this.props.value.getOrElse([]), sort.oldIndex, sort.newIndex)))
	};

	/**
	 * Convert element to it's display text for query input.
	 * @param {T} element element to convert.
	 * @return {string} display text from element.
	 */
	abstract elementToText(element: T): string;
}
